/** Divetta R7 — reveal lifecycle. HTML is readable before enhancement and after disposal.
 * Pattern references: REPO-CAP-0840 (inView), REPO-CAP-2631 (ScrollReveal).
 * Native adaptation; no React dependency or copied component source. */
(() => {
  'use strict';
  const KEY = '__DivettaR7Core';
  window[KEY]?.destroy?.();
  const life = new AbortController();
  const { signal } = life;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mounted = new Set();
  const pending = new Set();
  let revealObserver, themeObserver, stickyObserver, stickyResize, stickyElement;
  let stickyFrame = 0, expiry = 0, unsubscribe;
  let disposed = false;
  const canAnimate = () => (window.DivettaZorge?.canAnimate ?? !reduced.matches) && !window.Shopify?.designMode;
  const collect = (scope, selector) => [...(scope.matches?.(selector) ? [scope] : []), ...Array.from(scope.querySelectorAll?.(selector) || [])];
  const show = (item) => {
    item.classList.add('is-r7-visible');
    item.removeAttribute('data-r7-reveal-pending');
    pending.delete(item);
    revealObserver?.unobserve(item);
  };
  const showAll = () => { Array.from(pending).forEach(show); clearTimeout(expiry); expiry = 0; };
  const mountReveals = (scope) => {
    if (!revealObserver && 'IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) show(entry.target); });
      }, { rootMargin: '0px 0px 64px 0px', threshold: 0 });
    }
    const measured = collect(scope, '[data-r7-reveal]').filter((item) => !mounted.has(item)).map((item) => ({ item, rect: item.getBoundingClientRect() }));
    const animate = canAnimate() && !!revealObserver;
    measured.forEach(({ item, rect }) => {
      mounted.add(item);
      item.dataset.r7RevealMounted = 'true';
      if (!animate || item.classList.contains('is-r7-visible') || rect.top < innerHeight + 40 || item.contains(document.activeElement)) show(item);
      else {
        item.dataset.r7RevealPending = 'true';
        pending.add(item);
        revealObserver.observe(item);
      }
    });
    if (!animate) showAll();
    if (pending.size && !expiry) expiry = setTimeout(showAll, 6000);
  };
  const fallbackTheme = () => ['product', 'collection', 'search', 'cart'].includes(document.documentElement.dataset.ldPageType || document.body?.dataset.pageType) ? 'light' : 'dark';
  const applyTheme = (value) => {
    const theme = ['dark', 'light', 'wine', 'powder'].includes(value) ? value : fallbackTheme();
    if (document.documentElement.dataset.r7HeaderTheme === theme) return;
    document.documentElement.dataset.r7HeaderTheme = theme;
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'theme-color'; document.head.append(meta); }
    meta.content = { dark: '#120c0f', light: '#f7f1eb', wine: '#4a1f2c', powder: '#e9d8dd' }[theme];
  };
  const mountThemes = () => {
    themeObserver?.disconnect();
    const sections = Array.from(document.querySelectorAll('[data-r7-theme]'));
    applyTheme(sections[0]?.dataset.r7Theme || fallbackTheme());
    if (!('IntersectionObserver' in window)) return;
    const visible = new Map();
    themeObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) visible.set(entry.target, entry); else visible.delete(entry.target); });
      const selected = Array.from(visible.values()).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (selected) applyTheme(selected.target.dataset.r7Theme);
    }, { rootMargin: '-12% 0px -68% 0px', threshold: [0, .08, .25, .5] });
    sections.forEach((section) => themeObserver.observe(section));
  };
  const syncSticky = () => {
    stickyFrame = 0;
    const html = document.documentElement;
    const sticky = stickyElement;
    if (!sticky?.isConnected) { html.classList.remove('ld-sticky-cart-visible'); html.style.removeProperty('--r7-sticky-cart-height'); return; }
    const rect = sticky.getBoundingClientRect();
    const css = getComputedStyle(sticky);
    const visible = !sticky.hidden && css.display !== 'none' && css.visibility !== 'hidden' && Number(css.opacity) > .01 && rect.height > 20 && rect.bottom > 0 && rect.top < innerHeight;
    html.classList.toggle('ld-sticky-cart-visible', visible);
    const height = `${visible ? Math.ceil(rect.height) : 0}px`;
    if (html.style.getPropertyValue('--r7-sticky-cart-height') !== height) html.style.setProperty('--r7-sticky-cart-height', height);
  };
  const requestSticky = () => { if (!disposed && !stickyFrame) stickyFrame = requestAnimationFrame(syncSticky); };
  const mountSticky = () => {
    const next = document.querySelector('sticky-add-to-cart, [data-ld-sticky-cart], .sticky-add-to-cart');
    if (next !== stickyElement) {
      stickyObserver?.disconnect(); stickyResize?.disconnect(); stickyElement = next;
      if (next && 'MutationObserver' in window) { stickyObserver = new MutationObserver(requestSticky); stickyObserver.observe(next, { attributes: true, childList: true, subtree: true }); }
      if (next && 'ResizeObserver' in window) { stickyResize = new ResizeObserver(requestSticky); stickyResize.observe(next); }
    }
    requestSticky();
  };
  const closeDetails = (except) => document.querySelectorAll('details[open][data-r7-exclusive]').forEach((node) => { if (node !== except) node.open = false; });
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const cart = target.closest('[data-r7-cart-trigger]');
    if (cart) {
      const drawer = document.querySelector('cart-drawer-component#cart-drawer');
      if (typeof drawer?.open === 'function') { event.preventDefault(); drawer.open(); return; }
    }
    const utility = target.closest('[data-r7-open-collection-utility]');
    if (utility) { event.preventDefault(); document.dispatchEvent(new CustomEvent('r7:collection-utility:open', { detail: { trigger: utility } })); utility.closest('details')?.removeAttribute('open'); return; }
    const action = target.closest('[data-r7-scroll-target]');
    if (!action) return;
    let destination;
    try { destination = document.querySelector(action.dataset.r7ScrollTarget || ':not(*)'); } catch (_) { return; }
    if (destination) { event.preventDefault(); destination.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'start' }); action.closest('details')?.removeAttribute('open'); }
  }, { signal });
  document.addEventListener('focusin', (event) => { const target = event.target instanceof Element ? event.target.closest('[data-r7-reveal]') : null; if (target) show(target); }, { signal });
  document.addEventListener('toggle', (event) => { if (event.target instanceof HTMLDetailsElement && event.target.open && event.target.matches('[data-r7-exclusive]')) closeDetails(event.target); }, { capture: true, signal });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeDetails(); }, { signal });
  const mount = (scope = document) => {
    if (disposed) return;
    mounted.forEach((item) => { if (!item.isConnected) { revealObserver?.unobserve(item); pending.delete(item); mounted.delete(item); } });
    document.documentElement.classList.toggle('r7-motion-enabled', canAnimate());
    document.documentElement.classList.toggle('r7-motion-static', !canAnimate());
    mountThemes(); mountReveals(scope); mountSticky();
  };
  document.addEventListener('shopify:section:load', (event) => mount(event.target), { signal });
  document.addEventListener('shopify:section:unload', (event) => { collect(event.target, '[data-r7-reveal]').forEach((item) => { show(item); mounted.delete(item); }); requestSticky(); }, { signal });
  reduced.addEventListener?.('change', () => mount(), { signal });
  window.addEventListener('resize', requestSticky, { passive: true, signal });
  window.addEventListener('pageshow', () => { showAll(); mount(); }, { signal });
  if (window.DivettaZorge?.subscribe) unsubscribe = window.DivettaZorge.subscribe('shell-sticky-cart', syncSticky);
  else window.addEventListener('scroll', requestSticky, { passive: true, signal });
  window[KEY] = { mount, destroy() {
    disposed = true; life.abort(); showAll(); mounted.forEach(show); mounted.clear();
    revealObserver?.disconnect(); themeObserver?.disconnect(); stickyObserver?.disconnect(); stickyResize?.disconnect();
    unsubscribe?.(); cancelAnimationFrame(stickyFrame);
    document.documentElement.classList.remove('r7-motion-enabled', 'r7-motion-static'); delete window[KEY];
  } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true, signal }); else mount();
})();
